import React, { useState, useRef, useEffect } from 'react';
import { Bell, Search, User, ChevronDown, Menu, LogOut, Settings, UserCircle } from 'lucide-react';
import clsx from 'clsx';
import { useAuth } from '../../contexts/AuthContext';

export default function Header({ sidebarCollapsed, onMenuClick, title, subtitle }) {
  const { user, logout, isAdmin } = useAuth();
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const getUserInitials = () => {
    if (!user) return '?';
    const first = user.first_name?.[0] || '';
    const last = user.last_name?.[0] || '';
    return (first + last).toUpperCase() || '?';
  };

  const getUserDisplayName = () => {
    if (!user) return 'User';
    if (user.first_name && user.last_name) {
      return `${user.first_name} ${user.last_name}`;
    }
    return user.username || 'User';
  };

  const getUserRole = () => {
    if (!user?.apps) return '';
    const rmsApp = user.apps.find(app => app.app_name === 'RMS');
    return rmsApp?.role_name || '';
  };

  return (
    <header
      className={clsx(
        'fixed top-0 right-0 h-14 bg-gradient-to-r from-wrg-blue-800 to-wrg-blue-700 flex items-center justify-between px-5 z-30 transition-all duration-200',
        sidebarCollapsed ? 'left-14' : 'left-56'
      )}
    >
      {/* Left side - Title or breadcrumb */}
      <div className="flex items-center gap-4">
        <button
          onClick={onMenuClick}
          className="lg:hidden p-2 rounded text-white/70 hover:text-white hover:bg-white/10"
        >
          <Menu className="w-5 h-5" />
        </button>

        {title && (
          <div>
            <h1 className="text-lg font-semibold text-white tracking-tight">{title}</h1>
            {subtitle && <p className="text-xs text-wrg-blue-200">{subtitle}</p>}
          </div>
        )}
      </div>

      {/* Right side - Actions */}
      <div className="flex items-center gap-2">
        {/* Search */}
        <div className="hidden md:flex items-center gap-2.5 bg-white/10 rounded px-3 py-1.5 border border-white/20 focus-within:border-white/40 transition-colors">
          <Search className="w-4 h-4 text-white/60" />
          <input
            type="text"
            placeholder="Search..."
            className="bg-transparent border-none outline-none text-sm text-white placeholder-white/50 w-40"
          />
          <kbd className="hidden lg:inline-flex items-center gap-0.5 px-1.5 py-0.5 text-xs text-white/60 bg-white/10 rounded border border-white/20">
            <span className="text-xs">⌘</span>K
          </kbd>
        </div>

        {/* Notifications */}
        <button className="relative p-2 rounded text-white/70 hover:text-white hover:bg-white/10 transition-colors">
          <Bell className="w-5 h-5" />
          <span className="absolute top-1.5 right-1.5 w-2 h-2 bg-red-500 rounded-full ring-2 ring-wrg-blue-700" />
        </button>

        {/* User menu */}
        <div className="relative" ref={dropdownRef}>
          <button
            onClick={() => setDropdownOpen(!dropdownOpen)}
            className="flex items-center gap-2 p-1.5 rounded hover:bg-white/10 transition-colors group"
          >
            <div className="w-8 h-8 bg-white/20 rounded flex items-center justify-center">
              <span className="text-sm font-medium text-white">{getUserInitials()}</span>
            </div>
            <div className="hidden md:block text-left">
              <p className="text-sm font-medium text-white">{getUserDisplayName()}</p>
              <p className="text-xs text-white/60">{getUserRole() || (isAdmin ? 'Admin' : 'User')}</p>
            </div>
            <ChevronDown className={clsx(
              'w-4 h-4 text-white/50 group-hover:text-white/80 transition-transform',
              dropdownOpen && 'rotate-180'
            )} />
          </button>

          {/* Dropdown Menu */}
          {dropdownOpen && (
            <div className="absolute right-0 top-full mt-1.5 w-60 bg-white rounded-lg shadow-lg border border-gray-200 py-1.5 z-50">
              {/* User info header */}
              <div className="px-4 py-3 border-b border-gray-100">
                <p className="text-base font-medium text-gray-900">{getUserDisplayName()}</p>
                <p className="text-sm text-gray-500 truncate">{user?.username || user?.email}</p>
              </div>

              {/* Menu items */}
              <div className="py-1">
                <button
                  onClick={() => {
                    setDropdownOpen(false);
                    // TODO: Navigate to profile page
                  }}
                  className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  <UserCircle className="w-5 h-5 text-gray-400" />
                  Your Profile
                </button>
                <button
                  onClick={() => {
                    setDropdownOpen(false);
                    // TODO: Navigate to settings page
                  }}
                  className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  <Settings className="w-5 h-5 text-gray-400" />
                  Settings
                </button>
              </div>

              {/* Logout */}
              <div className="border-t border-gray-100 py-1">
                <button
                  onClick={() => {
                    setDropdownOpen(false);
                    logout();
                  }}
                  className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-red-600 hover:bg-red-50 transition-colors"
                >
                  <LogOut className="w-5 h-5" />
                  Sign out
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </header>
  );
}
